import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Separator } from '../components/ui/separator';
import BookingStatusBadge from '../components/ui/BookingStatusBadge';
import PaymentStatusBadge from '../components/ui/PaymentStatusBadge';
import RadixDialog from '../components/ui/RadixDialog';
import { getTripDetails, updateTripStatus, updateTripPnr } from '../services/api';
import { format } from 'date-fns';

/**
 * Booking Details page component
 * @returns {JSX.Element} BookingDetailsPage component
 */
const BookingDetailsPage = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  
  // State
  const [booking, setBooking] = useState(null);
  const [rawTripData, setRawTripData] = useState(null); // Store raw trip data for check-in navigation
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Dialog states
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [showTotalCostPriceDialog, setShowTotalCostPriceDialog] = useState(false);
  // Removed dialogs: payment status, refund, cancel

  // Form states
  const [newStatus, setNewStatus] = useState('');
  // removed: newPaymentStatus
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [totalCostPrice, setTotalCostPrice] = useState('');
  // removed: cancelReason, refundAmount, refundReason

  // Edit states
  const [isEditingPnr, setIsEditingPnr] = useState(false);
  const [editedPnr, setEditedPnr] = useState('');
  // UI states
  const [showSegments, setShowSegments] = useState(false);

  // Copy to clipboard function
  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here if desired
      console.log(`${fieldName} copied to clipboard: ${text}`);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  // Fields for status update (when BOOKING_SUCCESS is selected)
  const [updatePnr, setUpdatePnr] = useState('');
  const [updateBookingRef, setUpdateBookingRef] = useState('');
  const [fareType, setFareType] = useState('');
  const [ticketClass, setTicketClass] = useState('');
  // Baggage data as simple text inputs with value field only
  const [checkinBaggageValue, setCheckinBaggageValue] = useState('');
  const [cabinBaggageValue, setCabinBaggageValue] = useState('');

  // Transform API data (core/v1/trip/{tripId}) to booking format used by UI
  const transformTripToBooking = (tripData) => {
    // Safely read flight/journey/segment details
    const journeys = tripData?.selectedFlightOffer?.journey || [];
    const searchCriteria = tripData?.searchCriteria || {};

    // Flatten all segments across journeys
    const segments = journeys.flatMap((j) => {
      const journeyDeparture = j?.departure || {};
      const journeyArrival = j?.arrival || {};
      return (j?.segments || []).map((s) => {
        const dep = s?.departure || journeyDeparture;
        const arr = s?.arrival || journeyArrival;
        return {
          airline: s?.airlineIata || s?.airlineName || 'N/A',
          airlineName: s?.airlineName || 'N/A',
          flightNumber: s?.flightNumber || 'N/A',
          airlineIata: s?.airlineIata || 'N/A',
          from: dep?.airportIata || 'N/A',
          to: arr?.airportIata || 'N/A',
          departure: dep?.date || '',
          arrival: arr?.date || '',
          duration: s?.duration || j?.duration || '',
          aircraft: s?.aircraftType || ''
        };
      });
    });

    const firstSegment = segments?.[0] || {};
    const lastSegment = segments?.[segments.length - 1] || {};
    const originIata = firstSegment?.from || searchCriteria?.originAirport || 'N/A';
    const destinationIata = lastSegment?.to || searchCriteria?.destinationAirport || 'N/A';
    const departureDateTime = firstSegment?.departure || '';
    const arrivalDateTime = lastSegment?.arrival || '';

    // Determine payment status: prefer API-provided value from transaction and display directly
    const paymentStatus = (() => {
      const apiPayment = tripData?.transaction?.status;
      if (apiPayment) {
        return apiPayment.toString().toUpperCase();
      }
      // Fallback from legacy trip status when API value is absent
      const tripStatus = (tripData?.status || '').toUpperCase();
      if (tripStatus === 'PAYMENT_INITIATED') return 'INITIATED';
      if (tripStatus === 'BOOKING_SUCCESS') return 'COMPLETED';
      if (tripStatus === 'BOOKING_FAILED') return 'FAILED';
      if (tripStatus === 'PAYMENT_FAILED') return 'FAILED';
      if (tripStatus === 'PAYMENT_PROCESSING') return 'PROCESSING';
      return 'INITIATED';
    })();

    // Booking status should directly use API value
    const bookingStatus = (tripData?.status || '').toString().toUpperCase();

    // Build passengers array from travelerDetails
    const passengers = (tripData?.travelerDetails || []).map((trav) => {
      const firstName = trav?.name?.firstName || '';
      const lastName = trav?.name?.lastName || '';
      const gender = trav?.gender || '';
      const dateOfBirth = trav?.dateOfBirth || null;
      const documents = trav?.documents || [];
      const email = trav?.contact?.emailAddress || 'N/A';
      const phoneObj = trav?.contact?.phones?.[0];
      const countryCode = phoneObj?.countryCallingCode || '';
      const phoneNumber = phoneObj?.number || '';
      const phone = phoneObj
        ? `${phoneObj.countryCallingCode || ''}${phoneObj.number || ''}`.trim() || 'N/A'
        : 'N/A';
      return {
        name: `${firstName} ${lastName}`.trim() || 'N/A',
        firstName,
        lastName,
        gender,
        dateOfBirth,
        documents,
        email,
        phone,
        countryCode,
        phoneNumber
      };
    });

    return {
      id: tripData?.tripId,
      bookingStatus,
      paymentStatus,
      amount: parseFloat(tripData?.totalAmount) || parseFloat(tripData?.selectedFlightOffer?.totalAmount) || 0,
      totalCostPrice: tripData?.totalCostPrice || null,
      currency: tripData?.currency || 'INR',
      pnr: tripData?.pnr || 'N/A',
      bookingDate: tripData?.createdAt || null,
      // Prefer date-only from searchCriteria; otherwise derive from first segment
      flightDate: searchCriteria?.departureDate || (firstSegment?.departure ? `${firstSegment.departure}`.split(/[T ]/)[0] : null),
      flight: {
        number: firstSegment?.flightNumber || 'N/A',
        route: `${originIata} → ${destinationIata}`,
        departure: `${originIata}${departureDateTime ? ` ${departureDateTime}` : ''}`.trim(),
        arrival: `${destinationIata}${arrivalDateTime ? ` ${arrivalDateTime}` : ''}`.trim(),
        class: (searchCriteria?.class || 'economy').replace(/\b\w/g, (c) => c.toUpperCase())
      },
      segments,
      passengers: passengers.length > 0 ? passengers : [
        { name: 'N/A', firstName: '', lastName: '', gender: '', dateOfBirth: null, documents: [], email: 'N/A', phone: 'N/A', countryCode: '', phoneNumber: '' }
      ],
      notes: tripData?.notes || ''
    };
  };

  // Safe date-time formatter for non-ISO strings like "YYYY-MM-DD HH:mm"
  const formatDateTime = (value, pattern = 'dd MMM yyyy HH:mm') => {
    if (!value) return 'N/A';
    try {
      const isoLike = typeof value === 'string' && value.includes('T') ? value : `${value}`.replace(' ', 'T');
      return format(new Date(isoLike), pattern);
    } catch {
      return value;
    }
  };

  // Format ISO-8601 duration (e.g., 'P1DT2H30M', 'PT1H5M') as 'Xd Yh Zm'
  const formatDuration = (isoDuration) => {
    if (!isoDuration || typeof isoDuration !== 'string') return isoDuration || 'N/A';
    const match = isoDuration.match(/P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?/i);
    if (!match) return isoDuration;
    const days = parseInt(match[1] || '0', 10);
    const hours = parseInt(match[2] || '0', 10);
    const minutes = parseInt(match[3] || '0', 10);
    const parts = [];
    if (days) parts.push(`${days}d`);
    if (hours) parts.push(`${hours}h`);
    if (minutes || parts.length === 0) parts.push(`${minutes}m`);
    return parts.join(' ');
  };

  // Load booking data
  useEffect(() => {
    const loadBooking = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('Loading booking details for ID:', bookingId);

        // Attempt live API first
        let tripData = null;
        try {
          tripData = await getTripDetails(bookingId);
        } catch (apiErr) {
          console.warn('Falling back to local sample trip response due to API error:', apiErr?.message);
        }

        if (tripData && tripData.success && tripData.data) {
          const transformedBooking = transformTripToBooking(tripData.data);
          setBooking(transformedBooking);
          setRawTripData(tripData.data); // Store raw trip data for check-in navigation
          setNewStatus(transformedBooking.bookingStatus);
          setAdditionalNotes(tripData.data.additionalNotes || '');
          setTotalCostPrice(transformedBooking.totalCostPrice ? transformedBooking.totalCostPrice.toString() : '');
          setEditedPnr(transformedBooking.pnr || '');

          // Pre-populate new fields with existing data if available
          setUpdatePnr(transformedBooking.pnr || '');
          setUpdateBookingRef(tripData.data.bookingReference || '');
          setFareType(tripData.data.fareType || '');
          setTicketClass(tripData.data.ticketClass || transformedBooking.flight?.class || '');

          // Pre-populate baggage data using only the value field
          const baggageData = tripData.data.selectedFlightOffer?.baggage;
          if (baggageData) {
            // Check-in baggage - only use value field
            if (baggageData.check_in_baggage?.value) {
              setCheckinBaggageValue(baggageData.check_in_baggage.value);
            }
            // Cabin baggage - only use value field
            if (baggageData.cabin_baggage?.value) {
              setCabinBaggageValue(baggageData.cabin_baggage.value);
            }
          }
        } else {
          setError('Booking not found or invalid response');
        }
      } catch (err) {
        // As a hard fallback, always try rendering the sample to avoid empty page
        console.error('Error loading booking, rendering local sample:', err);
      } finally {
        setLoading(false);
      }
    };

    if (bookingId) {
      loadBooking();
    }
  }, [bookingId]);

  // Handle status change
  const handleStatusChange = async () => {
    try {
      if (!newStatus) return;

      // Validation for BOOKING_SUCCESS status
      if (newStatus === 'BOOKING_SUCCESS') {
        const errors = [];

        // PNR and Booking Reference validation - at least one required
        if (!updatePnr?.trim() && !updateBookingRef?.trim()) {
          errors.push('Either PNR or Booking Reference is required');
        }

        // Required fields validation
        if (!fareType?.trim()) {
          errors.push('Fare Type is required');
        }
        if (!ticketClass?.trim()) {
          errors.push('Ticket Class is required');
        }

        // Baggage validation - text input required
        if (!checkinBaggageValue?.trim()) {
          errors.push('Check-in Baggage is required');
        }
        if (!cabinBaggageValue?.trim()) {
          errors.push('Cabin Baggage is required');
        }

        if (errors.length > 0) {
          alert('Validation errors:\n' + errors.join('\n'));
          return;
        }
      }

      // Build payload
      const payload = { status: newStatus };
      if (newStatus === 'BOOKING_SUCCESS') {
        if (updatePnr?.trim()) payload.pnr = updatePnr.trim();
        if (updateBookingRef?.trim()) payload.bookingReference = updateBookingRef.trim();
        payload.fareType = fareType.trim();
        payload.ticketClass = ticketClass.trim();

        // Structured baggage data with value field
        payload.checkinBaggage = {
          value: checkinBaggageValue.trim()
        };
        payload.cabinBaggage = {
          value: cabinBaggageValue.trim()
        };
      }

      await updateTripStatus(booking.id, payload);
      // Refresh details
      const tripData = await getTripDetails(booking.id);
      if (tripData?.success && tripData?.data) {
        const transformed = transformTripToBooking(tripData.data);
        setBooking(transformed);
        setNewStatus(transformed.bookingStatus);
      }
      setShowStatusDialog(false);
    } catch (err) {
      console.error('Error updating status:', err);
      alert(err?.message || 'Failed to update status');
    }
  };

  // Handle email confirmation
  const handleSendEmail = async () => {
    try {
      // TODO: Implement API call to send email
      console.log('Sending email:', { subject: emailSubject, message: emailMessage });
      setShowEmailDialog(false);
      setEmailSubject('');
      setEmailMessage('');
    } catch (err) {
      console.error('Error sending email:', err);
    }
  };

  // Handle check-in navigation
  const handleCheckInNavigation = () => {
    if (!rawTripData) {
      alert('Trip data not available for check-in navigation');
      return;
    }

    // Extract ticketId and userId from raw trip data
    // Based on the API structure, ticketId might be the same as tripId or a separate field
    const ticketId = rawTripData.ticketId || rawTripData.tripId;
    const userId = rawTripData.userId;

    if (!ticketId) {
      alert('Ticket ID not available for check-in navigation');
      return;
    }

    if (!userId) {
      alert('User ID not available for check-in navigation');
      return;
    }

    console.log('Navigating to check-in with ticketId:', ticketId, 'userId:', userId);
    navigate(`/check-in/${ticketId}/${userId}`);
  };

  // Handle notes update
  const handleUpdateNotes = async () => {
    try {
      // Call the same update API with additionalNotes data
      const payload = { additionalNotes: additionalNotes };
      await updateTripStatus(booking.id, payload);

      // Refresh details to get updated data
      const tripData = await getTripDetails(booking.id);
      if (tripData?.success && tripData?.data) {
        const transformed = transformTripToBooking(tripData.data);
        setBooking(transformed);
      }
      setShowNotesDialog(false);
      console.log('Additional notes updated:', additionalNotes);
    } catch (err) {
      console.error('Error updating notes:', err);
      alert(err?.message || 'Failed to update notes');
    }
  };

  // Handle totalCostPrice update
  const handleUpdateTotalCostPrice = async () => {
    try {
      // Validate numeric input
      const numericValue = totalCostPrice.trim() === '' ? null : parseFloat(totalCostPrice);
      if (totalCostPrice.trim() !== '' && (isNaN(numericValue) || numericValue < 0)) {
        alert('Please enter a valid positive number for Total Cost Price');
        return;
      }

      // Call the same update API with totalCostPrice data
      const payload = { totalCostPrice: numericValue };
      await updateTripStatus(booking.id, payload);

      // Refresh details to get updated data
      const tripData = await getTripDetails(booking.id);
      if (tripData?.success && tripData?.data) {
        const transformed = transformTripToBooking(tripData.data);
        setBooking(transformed);
        setTotalCostPrice(transformed.totalCostPrice ? transformed.totalCostPrice.toString() : '');
      }
      setShowTotalCostPriceDialog(false);
      console.log('Total cost price updated:', numericValue);
    } catch (err) {
      console.error('Error updating total cost price:', err);
      alert(err?.message || 'Failed to update total cost price');
    }
  };

  // Handle payment status change
  // removed: handlePaymentStatusChange

  // Handle booking cancellation
  // removed: handleCancelBooking

  // Handle refund processing
  // removed: handleProcessRefund

  // Handle WhatsApp message
  // const handleSendWhatsApp = () => {
  //   const phoneNumber = booking.passengers?.[0]?.phone || '';
  //   if (!phoneNumber) {
  //     alert('No phone number available for this passenger');
  //     return;
  //   }
  //   const passengerName = booking.passengers?.[0]
  //     ? `${booking.passengers[0].firstName || ''} ${booking.passengers[0].lastName || ''}`.trim() || 'Passenger'
  //     : 'Passenger';
  //   const message = `Hello ${passengerName}, your booking ${booking.id} for flight ${booking.flight?.airline} ${booking.flight?.number} on ${booking.flightDate ? format(new Date(booking.flightDate), 'dd MMM yyyy') : 'N/A'} has been confirmed. PNR: ${booking.pnr}`;
  //   const whatsappUrl = `https://wa.me/${phoneNumber.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
  //   window.open(whatsappUrl, '_blank');
  // };

  // Handle ticket download
  // const handleDownloadTicket = () => {
  //   // TODO: Implement ticket download functionality
  //   console.log('Downloading ticket for booking:', booking.id);
  // };

  // Removed: handlePrintTicket


  // Handle PNR editing
  const handleEditPnr = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setEditedPnr(booking.pnr || ''); // Pre-populate with current PNR value
    setIsEditingPnr(true);
  };

  const handleSavePnr = async () => {
    try {
      if (!editedPnr?.trim()) {
        alert('PNR cannot be empty');
        return;
      }

      // Call API to update PNR
      await updateTripPnr(booking.id, editedPnr.trim());

      // Update local state
      setBooking(prev => ({ ...prev, pnr: editedPnr.trim() }));
      setIsEditingPnr(false);

      console.log('PNR updated successfully:', editedPnr);
      // Optional: Show success message
      // alert('PNR updated successfully');
    } catch (err) {
      console.error('Error updating PNR:', err);
      alert(err?.message || 'Failed to update PNR. Please try again.');
    }
  };

  const handleCancelEditPnr = () => {
    setEditedPnr(booking.pnr || '');
    setIsEditingPnr(false);
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading booking details...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error || !booking) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error || 'Booking not found'}</p>
            <Button onClick={() => navigate('/bookings')} variant="outline">
              Back to Bookings
            </Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="flex flex-col h-full overflow-hidden">
        {/* Header - Fixed height */}
        <div className="flex-shrink-0 px-4 py-3 border-b bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-xl font-bold text-gray-900">Booking Details</h1>
                <p className="text-sm text-gray-600">Booking ID: {booking.id}</p>
              </div>
            </div>

            <div className="flex flex-col items-end gap-1">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Booking</span>
                <BookingStatusBadge status={booking.bookingStatus} />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Payment</span>
                <PaymentStatusBadge status={booking.paymentStatus} />
              </div>
            </div>
          </div>
        </div>

        {/* Main Content - Scrollable */}
        <div className="flex-1 overflow-auto p-4">

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-full">
            {/* Left Column - Booking Information */}
            <div className="lg:col-span-2 overflow-auto">
              <Accordion type="multiple" defaultValue={["flight-info"]} className="space-y-3">
                {/* Flight Information Accordion */}
                <AccordionItem value="flight-info" className="bg-white rounded-lg border border-gray-200">
                  <AccordionTrigger className="px-4 py-3 text-base font-semibold">
                    Flight Information
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                  {/* Summary row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Route</label>
                      <p className="text-lg">{booking.flight?.route}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Class</label>
                      <p className="text-lg">{booking.flight?.class}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Flight Date</label>
                      <p className="text-lg">{booking.flightDate ? format(new Date(booking.flightDate), 'dd MMM yyyy') : 'N/A'}</p>
                    </div>
                  </div>

                  {/* Segments dropdown */}
                  <div className="mt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowSegments(prev => !prev)}
                      className="mb-3"
                    >
                      {showSegments ? 'Hide Segments' : `Show Segments (${booking?.segments?.length || 0})`}
                    </Button>

                    {showSegments && (
                      <div className="space-y-3">
                        {booking?.segments?.map((seg, idx) => (
                          <div key={idx} className="border border-gray-100 rounded-lg p-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <label className="text-sm font-medium text-gray-700">Flight</label>
                                <p className="text-lg font-semibold">{seg.airlineIata}{seg.flightNumber}</p>
                              </div>
                              <div>
                                <label className="text-sm font-medium text-gray-700">From → To</label>
                                <p className="text-lg">{seg.from} → {seg.to}</p>
                              </div>
                              <div>
                                <label className="text-sm font-medium text-gray-700">Airline</label>
                                <p className="text-lg">{seg.airlineName || 'N/A'}</p>
                              </div>
                              <div>
                                <label className="text-sm font-medium text-gray-700">Departure</label>
                                <p className="text-lg">{formatDateTime(seg.departure)}</p>
                              </div>
                              <div>
                                <label className="text-sm font-medium text-gray-700">Arrival</label>
                                <p className="text-lg">{formatDateTime(seg.arrival)}</p>
                              </div>
                              <div>
                                <label className="text-sm font-medium text-gray-700">Duration</label>
                                <p className="text-lg">{formatDuration(seg.duration)}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                        {!booking?.segments?.length && (
                          <p className="text-gray-500">No segment information available.</p>
                        )}
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>

                {/* Passenger Information Accordion */}
                <AccordionItem value="passenger-info" className="bg-white rounded-lg border border-gray-200">
                  <AccordionTrigger className="px-4 py-3 text-base font-semibold">
                    Passenger Information
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="space-y-3">
                      {booking.passengers?.map((passenger, index) => (
                        <div key={index} className="border border-gray-100 rounded-lg p-4">
                          {/* Primary passenger information */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                            <div>
                              <label className="text-xs font-medium text-gray-700">First Name</label>
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium">{passenger.firstName || 'N/A'}</p>
                                {passenger.firstName && (
                                  <button
                                    onClick={() => copyToClipboard(passenger.firstName, 'First Name')}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                    title="Copy first name"
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </div>
                            <div>
                              <label className="text-xs font-medium text-gray-700">Last Name</label>
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium">{passenger.lastName || 'N/A'}</p>
                                {passenger.lastName && (
                                  <button
                                    onClick={() => copyToClipboard(passenger.lastName, 'Last Name')}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                    title="Copy last name"
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </div>
                            <div>
                              <label className="text-xs font-medium text-gray-700">Gender</label>
                              <p className="text-sm">{passenger.gender || 'N/A'}</p>
                            </div>
                          </div>

                          {/* Contact information */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                            <div>
                              <label className="text-xs font-medium text-gray-700">Email</label>
                              <div className="flex items-center gap-2">
                                <p className="text-sm">{passenger.email}</p>
                                {passenger.email && passenger.email !== 'N/A' && (
                                  <button
                                    onClick={() => copyToClipboard(passenger.email, 'Email')}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                    title="Copy email"
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </div>
                            {passenger.dateOfBirth && (
                              <div>
                                <label className="text-xs font-medium text-gray-700">Date of Birth</label>
                                <p className="text-sm">{formatDateTime(passenger.dateOfBirth, 'dd MMM yyyy')}</p>
                              </div>
                            )}
                          </div>

                          {/* Phone information - split into country code and number */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                            <div>
                              <label className="text-xs font-medium text-gray-700">Country Code</label>
                              <div className="flex items-center gap-2">
                                <p className="text-sm">{passenger.countryCode ? `+${passenger.countryCode}` : 'N/A'}</p>
                                {passenger.countryCode && (
                                  <button
                                    onClick={() => copyToClipboard(`+${passenger.countryCode}`, 'Country Code')}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                    title="Copy country code"
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </div>
                            <div>
                              <label className="text-xs font-medium text-gray-700">Mobile Number</label>
                              <div className="flex items-center gap-2">
                                <p className="text-sm">{passenger.phoneNumber || 'N/A'}</p>
                                {passenger.phoneNumber && (
                                  <button
                                    onClick={() => copyToClipboard(passenger.phoneNumber, 'Mobile Number')}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                    title="Copy mobile number"
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </div>
                            <div>
                              <label className="text-xs font-medium text-gray-700">Complete Number</label>
                              <div className="flex items-center gap-2">
                                <p className="text-sm">{passenger.phone || 'N/A'}</p>
                                {passenger.phone && passenger.phone !== 'N/A' && (
                                  <button
                                    onClick={() => copyToClipboard(passenger.phone, 'Complete Phone Number')}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                    title="Copy complete phone number"
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Documents section - only show if documents exist */}
                          {passenger.documents && passenger.documents.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-gray-100">
                              <label className="text-xs font-medium text-gray-700 block mb-2">Documents</label>
                              <div className="space-y-2">
                                {passenger.documents.map((doc, docIndex) => (
                                  <div key={docIndex} className="bg-gray-50 rounded p-2">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                                      {doc.documentType && (
                                        <div>
                                          <span className="font-medium text-gray-600">Type:</span>
                                          <span className="ml-1">{doc.documentType}</span>
                                        </div>
                                      )}
                                      {doc.number && (
                                        <div>
                                          <span className="font-medium text-gray-600">Number:</span>
                                          <span className="ml-1">{doc.number}</span>
                                        </div>
                                      )}
                                      {doc.expiryDate && (
                                        <div>
                                          <span className="font-medium text-gray-600">Expiry:</span>
                                          <span className="ml-1">{formatDateTime(doc.expiryDate, 'dd MMM yyyy')}</span>
                                        </div>
                                      )}
                                      {doc.issuanceCountry && (
                                        <div>
                                          <span className="font-medium text-gray-600">Country:</span>
                                          <span className="ml-1">{doc.issuanceCountry}</span>
                                        </div>
                                      )}
                                      {doc.nationality && (
                                        <div>
                                          <span className="font-medium text-gray-600">Nationality:</span>
                                          <span className="ml-1">{doc.nationality}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Payment Details Accordion */}
                <AccordionItem value="payment-details" className="bg-white rounded-lg border border-gray-200">
                  <AccordionTrigger className="px-4 py-3 text-base font-semibold">
                    Payment Details
                    <Button
                      onClick={handleEditPnr}
                      variant="outline"
                      size="sm"
                      className="ml-auto mr-4"
                      disabled={isEditingPnr}
                    >
                      {isEditingPnr ? 'Editing...' : 'Edit PNR'}
                    </Button>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">PNR</label>
                      {isEditingPnr ? (
                        <div className="mt-1">
                          <Input
                            value={editedPnr}
                            onChange={(e) => setEditedPnr(e.target.value)}
                            className="font-mono"
                            placeholder="Enter PNR"
                          />
                          <div className="flex gap-2 mt-2">
                            <Button onClick={handleSavePnr} size="sm">
                              Save
                            </Button>
                            <Button onClick={handleCancelEditPnr} variant="outline" size="sm">
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-lg font-mono">{booking.pnr}</p>
                      )}
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Booking Date</label>
                      <p className="text-lg">{booking.bookingDate ? format(new Date(booking.bookingDate), 'dd MMM yyyy') : 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Amount</label>
                      <p className="text-lg font-semibold">{booking.currency} {booking.amount?.toLocaleString()}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Total Cost Price</label>
                      <div className="flex items-center gap-2">
                        <p className="text-lg font-semibold">
                          {booking.totalCostPrice !== null && booking.totalCostPrice !== undefined
                            ? `${booking.currency} ${parseFloat(booking.totalCostPrice).toLocaleString()}`
                            : 'Not set'}
                        </p>
                        <Button
                          onClick={() => setShowTotalCostPriceDialog(true)}
                          variant="outline"
                          size="sm"
                        >
                          Edit
                        </Button>
                      </div>
                    </div>
                    <div className="mb-4">
                      <label className="text-sm font-medium text-gray-700 block mb-1">Payment Status</label>
                      <PaymentStatusBadge status={booking.paymentStatus} />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Parsed Text / Raw Data Accordion - Closed by default */}
              <AccordionItem value="parsed-text" className="bg-white rounded-lg border border-gray-200">
                <AccordionTrigger className="px-4 py-3 text-base font-semibold">
                  Parsed Text (Raw Data)
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <h3 className="text-sm font-medium text-gray-700">Raw Trip Data</h3>
                      {rawTripData && (
                        <button
                          onClick={() => copyToClipboard(JSON.stringify(rawTripData, null, 2), 'Raw Trip Data')}
                          className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a-2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          Copy JSON
                        </button>
                      )}
                    </div>
                    {rawTripData ? (
                      <div className="bg-gray-50 p-4 rounded-md text-sm text-gray-700 overflow-auto max-h-96">
                        <pre className="whitespace-pre-wrap break-all text-xs">
                          {JSON.stringify(rawTripData, null, 2)}
                        </pre>
                      </div>
                    ) : (
                      <div className="bg-gray-50 p-4 rounded-md text-sm text-gray-500">
                        No raw data available.
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
              </Accordion>
            </div>

            {/* Right Column - Actions */}
            <div className="space-y-4 overflow-auto">
              {/* User Information Card */}
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h2 className="text-base font-semibold mb-2">User</h2>
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    <span className="font-medium">User ID:</span> {rawTripData?.userId || 'N/A'}
                  </div>
                  {rawTripData?.userId && (
                    <button
                      onClick={() => copyToClipboard(rawTripData.userId, 'User ID')}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      title="Copy user ID"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              {/* Quick Actions Card */}
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h2 className="text-base font-semibold mb-3">Quick Actions</h2>
                <div className="space-y-2">
                <Button
                  onClick={handleCheckInNavigation}
                  className="w-full justify-start"
                  variant="outline"
                  disabled={!rawTripData}
                >
                  ✈️ Go to Check-in
                </Button>

                <Button
                  onClick={() => setShowStatusDialog(true)}
                  className="w-full justify-start"
                  variant="outline"
                >
                  📧 Change Booking Status
                </Button>

                {/* Removed: Change Payment Status */}

                <Separator />

                {/* <Button
                  onClick={() => {
                    setEmailSubject(`Booking Confirmation - ${booking.id}`);
                    const emailPassengerName = booking.passengers?.[0]
                      ? `${booking.passengers[0].firstName || ''} ${booking.passengers[0].lastName || ''}`.trim() || 'Passenger'
                      : 'Passenger';
                    setEmailMessage(`Dear ${emailPassengerName},\n\nYour booking ${booking.id} has been confirmed.\n\nFlight Details:\n${booking.flight?.airline} ${booking.flight?.number}\n${booking.flight?.route}\nDate: ${booking.flightDate ? format(new Date(booking.flightDate), 'dd MMM yyyy') : 'N/A'}\n\nThank you for choosing us.`);
                    setShowEmailDialog(true);
                  }}
                  className="w-full justify-start"
                  variant="outline"
                >
                  📧 Send Email Confirmation
                </Button>

                <Button
                  onClick={handleSendWhatsApp}
                  className="w-full justify-start"
                  variant="outline"
                >
                  💬 Send WhatsApp Message
                </Button>

                <Separator />

                <Button
                  onClick={handleDownloadTicket}
                  className="w-full justify-start"
                  variant="outline"
                >
                  📄 Download Ticket
                </Button> */}

                {/* Removed: Print Ticket */}

                <Button
                  onClick={() => setShowNotesDialog(true)}
                  className="w-full justify-start"
                  variant="outline"
                >
                  📝 Add/Edit Notes
                </Button>

                <Separator />

                {/* Removed: Process Refund */}

                {/* Removed: Cancel Booking */}
              </div>
            </div>

              {/* Notes Card */}
              {booking.notes && (
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <h2 className="text-base font-semibold mb-2">Notes</h2>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{booking.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Status Change Dialog */}
        <RadixDialog
          open={showStatusDialog}
          onOpenChange={setShowStatusDialog}
          title="Change Booking Status"
          description="Select a new status for this booking"
          footer={
            <>
              <Button variant="outline" onClick={() => setShowStatusDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleStatusChange}>
                Update Status
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Current Status: <BookingStatusBadge status={booking.bookingStatus} />
              </label>
              <Select value={newStatus} onValueChange={(v) => { setNewStatus(v); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Select new status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="INITIALISED">INITIALISED</SelectItem>
                  <SelectItem value="BOOKING_INITIALISED">BOOKING_INITIALISED</SelectItem>
                  <SelectItem value="BOOKING_SUCCESS">BOOKING_SUCCESS</SelectItem>
                  <SelectItem value="BOOKING_FAILED">BOOKING_FAILED</SelectItem>
                  <SelectItem value="BOOKING_ERROR">BOOKING_ERROR</SelectItem>
                  <SelectItem value="PAYMENT_REFUNDED">PAYMENT_REFUNDED</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {newStatus === 'BOOKING_SUCCESS' && (
              <div className="space-y-4">
                {/* PNR and Booking Reference - Mutually Exclusive but at least one required */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 flex items-center">
                      PNR
                      {(!updatePnr?.trim() && !updateBookingRef?.trim()) && <span className="text-red-500 ml-1">*</span>}
                      {updatePnr?.trim() && !updateBookingRef?.trim() && <span className="text-red-500 ml-1 text-xs">🔴</span>}
                    </label>
                    <Input value={updatePnr} onChange={(e) => setUpdatePnr(e.target.value)} placeholder="Enter PNR" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 flex items-center">
                      Booking Reference
                      {(!updatePnr?.trim() && !updateBookingRef?.trim()) && <span className="text-red-500 ml-1">*</span>}
                      {!updatePnr?.trim() && updateBookingRef?.trim() && <span className="text-red-500 ml-1 text-xs">🔴</span>}
                    </label>
                    <Input value={updateBookingRef} onChange={(e) => setUpdateBookingRef(e.target.value)} placeholder="Enter Booking Reference" />
                  </div>
                </div>

                {/* Fare Type and Ticket Class */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Fare Type <span className="text-red-500">*</span></label>
                    <Input
                      value={fareType}
                      onChange={(e) => setFareType(e.target.value)}
                      placeholder="Enter fare type (e.g., Economy, Business)"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Ticket Class <span className="text-red-500">*</span></label>
                    <Input
                      value={ticketClass}
                      onChange={(e) => setTicketClass(e.target.value)}
                      placeholder="Enter ticket class (e.g., Economy, Business)"
                    />
                  </div>
                </div>

                {/* Baggage Information */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Baggage Information</label>

                  {/* Check-in Baggage */}
                  <div className="mb-4">
                    <label className="text-sm font-medium text-gray-600 mb-2 block">Check-in Baggage <span className="text-red-500">*</span></label>
                    <Input
                      value={checkinBaggageValue}
                      onChange={(e) => setCheckinBaggageValue(e.target.value)}
                      placeholder="Enter check-in baggage details (e.g., 15 KG, 2 pieces)"
                    />
                  </div>

                  {/* Cabin Baggage */}
                  <div>
                    <label className="text-sm font-medium text-gray-600 mb-2 block">Cabin Baggage <span className="text-red-500">*</span></label>
                    <Input
                      value={cabinBaggageValue}
                      onChange={(e) => setCabinBaggageValue(e.target.value)}
                      placeholder="Enter cabin baggage details (e.g., 7 KG, 1 piece)"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </RadixDialog>

        {/* Email Dialog */}
        <RadixDialog
          open={showEmailDialog}
          onOpenChange={setShowEmailDialog}
          title="Send Email Confirmation"
          description="Send a confirmation email to the passenger"
          maxWidth="lg"
          footer={
            <>
              <Button variant="outline" onClick={() => setShowEmailDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSendEmail}>
                Send Email
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                To: {booking.passengers?.map(p => p.email).join(', ')}
              </label>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Subject</label>
              <Input
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Email subject"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Message</label>
              <Textarea
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                placeholder="Email message"
                rows={8}
              />
            </div>
          </div>
        </RadixDialog>

        {/* Notes Dialog */}
        <RadixDialog
          open={showNotesDialog}
          onOpenChange={setShowNotesDialog}
          title="Add/Edit Additional Notes"
          description="Add internal additional notes for this booking"
          footer={
            <>
              <Button variant="outline" onClick={() => setShowNotesDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateNotes}>
                Save Notes
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Additional Notes</label>
              <Textarea
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
                placeholder="Add internal additional notes about this booking..."
                rows={6}
              />
            </div>
          </div>
        </RadixDialog>

        {/* Total Cost Price Dialog */}
        <RadixDialog
          open={showTotalCostPriceDialog}
          onOpenChange={setShowTotalCostPriceDialog}
          title="Update Total Cost Price"
          description="Update the total cost price for this booking"
          footer={
            <>
              <Button variant="outline" onClick={() => setShowTotalCostPriceDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateTotalCostPrice}>
                Update Total Cost Price
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Total Cost Price ({booking.currency})
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={totalCostPrice}
                onChange={(e) => setTotalCostPrice(e.target.value)}
                placeholder="Enter total cost price (leave empty to clear)"
              />
              <p className="text-xs text-gray-500 mt-1">
                Current value: {booking.totalCostPrice !== null && booking.totalCostPrice !== undefined
                  ? `${booking.currency} ${parseFloat(booking.totalCostPrice).toLocaleString()}`
                  : 'Not set'}
              </p>
            </div>
          </div>
        </RadixDialog>

        {/* Removed: Payment Status, Cancel Booking, and Refund Dialogs */}
      </div>
    </MainLayout>
  );
};

export default BookingDetailsPage;
